import { animate, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChip, MatChipList } from '@angular/material/chips';
import { DirectusCheckboxChoice } from '@interfaces';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-chips-select',
  templateUrl: './chips-select.component.html',
  styleUrls: ['./chips-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ChipsSelectComponent,
      multi: true,
    },
  ],
  animations: [
    trigger('fadeInAndOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(-20px)' })),
      ]),
    ]),
  ],
})
export class ChipsSelectComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() options: DirectusCheckboxChoice[] = [];
  @Input() multiple = true; // default behavior remains multiple
  @Input() allowOther = false; // do not allow "Other" option by default
  @Input() disabled = false;

  @ViewChild(MatChipList)
  chipList!: MatChipList;
  @ViewChild('otherInput') otherInput!: ElementRef<HTMLInputElement>;

  public displayOptions: DirectusCheckboxChoice[] = [];
  public showOtherInput: boolean = false;
  public otherInputValue = '';
  public currentOtherValue = '';

  // internal value: either string[] (multiple) or string | null (single)
  value: string[] | string | null = this.multiple ? [] : null;
  onChange!: (value: any) => void;
  onTouch: any;

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    this.updateDisplayOptions();
  }

  ngAfterViewInit() {
    this.selectChips(this.value as any);

    this.chipList.chipSelectionChanges
      .pipe(
        untilDestroyed(this),
        map((event) => event.source),
      )
      .subscribe((chip: MatChip) => {
        this.handleChipSelection(chip);
      });
  }

  private updateDisplayOptions() {
    this.displayOptions = this.allowOther
      ? [
          ...this.options,
          { text: this.translateService.instant('form.other.chip'), value: '__other__' },
        ]
      : [...this.options];
  }

  private handleChipSelection(chip: MatChip) {
    if (chip.value === '__other__') {
      this.handleOtherChipSelection(chip);
    } else {
      this.handleRegularChipSelection(chip);
    }
  }

  private handleOtherChipSelection(chip: MatChip) {
    if (chip.selected) {
      this.showOtherInput = true;
      this.otherInputValue = this.currentOtherValue;
      // Focus input after view updates
      setTimeout(() => {
        if (this.otherInput) {
          this.otherInput.nativeElement.focus();
        }
      }, 100);
    } else {
      this.hideOtherInput();
    }
  }

  private handleRegularChipSelection(chip: MatChip) {
    if (this.multiple) {
      if (chip.selected) {
        const arr = Array.isArray(this.value) ? [...(this.value as string[])] : [];
        if (!arr.includes(chip.value)) {
          (this.value as string[]) = [...arr, chip.value];
        }
      } else {
        (this.value as string[]) = (this.value as string[]).filter((val) => val !== chip.value);
      }
      this.propagateChange(this.value as string[]);
    } else {
      if (chip.selected) {
        this.chipList.chips.forEach((c) => {
          if (c !== chip) c.deselect();
        });
        this.value = chip.value;
        this.propagateChange(this.value as string);
      } else {
        this.value = null;
        this.propagateChange(this.value);
      }
    }
  }

  public saveOtherValue() {
    const trimmedValue = this.otherInputValue.trim();

    if (!trimmedValue) {
      this.hideOtherInput();
      return;
    }

    this.currentOtherValue = trimmedValue;

    if (this.multiple) {
      const currentValues = Array.isArray(this.value) ? [...(this.value as string[])] : [];
      // Remove any previous other values
      const filteredValues = currentValues.filter((val) => this.isKnownOption(val));
      this.value = [...filteredValues, trimmedValue];
    } else {
      this.value = trimmedValue;
    }

    this.propagateChange(this.value);
  }

  private hideOtherInput() {
    this.showOtherInput = false;
    this.otherInputValue = '';

    if (this.multiple) {
      // Only keep values that are from options
      const currentValues = Array.isArray(this.value) ? (this.value as string[]) : [];
      this.value = currentValues.filter((val) => this.isKnownOption(val));
    } else {
      // Clear value if it's an other value
      if (this.value && !this.isKnownOption(this.value as string)) {
        this.value = null;
      }
    }

    this.currentOtherValue = '';
    this.propagateChange(this.value);

    // Deselect other chip
    const otherChip = this.chipList?.chips.find((chip) => chip.value === '__other__');
    if (otherChip) {
      otherChip.deselect();
    }
  }

  private isKnownOption(value: string): boolean {
    return this.options.some((option) => option.value === value);
  }

  public getChipDisplayText(option: DirectusCheckboxChoice): string {
    if (option.value === '__other__') {
      return this.currentOtherValue ? `Other: ${this.currentOtherValue}` : 'Other';
    }
    return option.text;
  }

  writeValue(value: string[] | string | null): void {
    if (this.multiple) {
      if (Array.isArray(value)) {
        this.value = value;
      } else if (value == null) {
        this.value = [];
      } else {
        this.value = [String(value)];
      }
    } else {
      if (Array.isArray(value)) {
        this.value = value.length ? String(value[0]) : null;
      } else {
        this.value = value == null ? null : String(value);
      }
    }

    // Check if there are values outside options ("other")
    this.updateOtherState();

    if (this.chipList) {
      this.selectChips(this.value);
    }
  }

  private updateOtherState() {
    if (!this.allowOther) return;

    if (this.multiple) {
      const values = Array.isArray(this.value) ? (this.value as string[]) : [];
      const otherValue = values.find((val) => !this.isKnownOption(val));
      if (otherValue) {
        this.currentOtherValue = otherValue;
      }
    } else {
      const val = this.value as string;
      if (val && !this.isKnownOption(val)) {
        this.currentOtherValue = val;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  propagateChange(value: string[] | string | null) {
    if (this.onChange) {
      this.onChange(value);
    }
  }

  selectChips(value: string[] | string | null): void {
    if (!this.chipList) return;

    // clear selections first
    this.chipList.chips.forEach((chip) => chip.deselect());

    if (this.multiple) {
      const want = Array.isArray(value) ? value : [];
      const chipsToSelect = this.chipList.chips.filter((chip) => want.includes(chip.value));
      chipsToSelect.forEach((chip) => chip.select());
    } else {
      const want = value == null ? null : String(value);
      const chipToSelect = this.chipList.chips.find((chip) => String(chip.value) === want);
      if (chipToSelect) chipToSelect.select();
    }
  }

  toggleSelection(chip: MatChip) {
    if (this.disabled) return;

    if (this.multiple) {
      chip.toggleSelected();
    } else {
      // single mode: selecting toggles to selected; clicking a selected chip will deselect it
      if (!chip.selected) {
        this.chipList.chips.forEach((c) => {
          if (c !== chip) c.deselect();
        });
        chip.select();
      } else {
        chip.deselect();
      }
    }
  }
}
